import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { MapPin, Loader2, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

const DEFAULT_CENTER = [78.9629, 20.5937]; // [lng, lat] — India center

function waitForMapplsSdk(maxMs = 20000) {
  return new Promise((resolve, reject) => {
    const started = Date.now();
    const tick = () => {
      if (window.mappls?.Map && window.mappls?.Marker) {
        resolve(window.mappls);
        return;
      }
      if (Date.now() - started >= maxMs) {
        reject(new Error('Mappls SDK not ready'));
        return;
      }
      window.setTimeout(tick, 150);
    };
    tick();
  });
}

function toMapplsCenter(lat, lng) {
  return [lng, lat];
}

function isMapplsMapReady(map) {
  if (!map) return false;
  if (typeof map.getCanvasContainer === 'function') return true;
  if (typeof map.isStyleLoaded === 'function' && map.isStyleLoaded()) return true;
  return false;
}

class MapPickerErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, retryKey: 0 };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch() {
    /* Prevent Mappls SDK errors from crashing the page */
  }

  handleRetry = () => {
    this.setState({ hasError: false, retryKey: this.state.retryKey + 1 });
  };

  render() {
    if (this.state.hasError) {
      return this.props.fallback(this.handleRetry);
    }
    const { fallback, ...pickerProps } = this.props;
    return <LocationMapPickerInner key={this.state.retryKey} {...pickerProps} />;
  }
}

function LocationMapPickerInner({ latitude, longitude, onChange, className }) {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const mapLoadedRef = useRef(false);
  const placedCoordsRef = useRef('');
  const bootGenerationRef = useRef(0);
  const latitudeRef = useRef(latitude);
  const longitudeRef = useRef(longitude);
  const onChangeRef = useRef(onChange);
  const mapContainerId = useId().replace(/:/g, '');
  const [status, setStatus] = useState('loading');
  const [errorMessage, setErrorMessage] = useState('');

  latitudeRef.current = latitude;
  longitudeRef.current = longitude;
  onChangeRef.current = onChange;

  const latNum = Number.parseFloat(latitude);
  const lngNum = Number.parseFloat(longitude);
  const hasCoords = Number.isFinite(latNum) && Number.isFinite(lngNum);

  const readCenter = useCallback(() => {
    const lat = Number.parseFloat(latitudeRef.current);
    const lng = Number.parseFloat(longitudeRef.current);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return { lat, lng, hasCoords: true };
    }
    return { lat: DEFAULT_CENTER[1], lng: DEFAULT_CENTER[0], hasCoords: false };
  }, []);

  const emitCoords = useCallback((lat, lng) => {
    const latFixed = Number(lat.toFixed(6));
    const lngFixed = Number(lng.toFixed(6));
    onChangeRef.current?.({
      latitude: String(latFixed),
      longitude: String(lngFixed),
      geocoordinates: `${latFixed},${lngFixed}`,
    });
  }, []);

  const clearMapOverlays = useCallback(() => {
    try {
      markerRef.current?.remove?.();
    } catch {
      /* ignore */
    }
    markerRef.current = null;
    mapLoadedRef.current = false;
    placedCoordsRef.current = '';
  }, []);

  const destroyMapInstance = useCallback(() => {
    clearMapOverlays();
    const map = mapInstanceRef.current;
    mapInstanceRef.current = null;
    if (map) {
      try {
        map.remove?.();
      } catch {
        /* ignore */
      }
    }
  }, [clearMapOverlays]);

  const setMapplsMarker = useCallback(
    (markerLat, markerLng) => {
      const map = mapInstanceRef.current;
      if (!map || !mapLoadedRef.current || !isMapplsMapReady(map)) {
        return;
      }

      try {
        markerRef.current?.remove?.();
      } catch {
        /* ignore */
      }

      markerRef.current = new window.mappls.Marker({
        map,
        position: { lat: markerLat, lng: markerLng },
        draggable: true,
        width: 32,
        height: 32,
      });

      markerRef.current.on?.('dragend', () => {
        const pos = markerRef.current.getPosition?.() || markerRef.current.getLatLng?.();
        const nextLat = pos?.lat ?? pos?.latitude ?? markerLat;
        const nextLng = pos?.lng ?? pos?.longitude ?? markerLng;
        placedCoordsRef.current = `${nextLat},${nextLng}`;
        emitCoords(nextLat, nextLng);
      });

      placedCoordsRef.current = `${markerLat},${markerLng}`;
    },
    [emitCoords]
  );

  const createMap = useCallback(async () => {
    const generation = ++bootGenerationRef.current;
    destroyMapInstance();
    setStatus('loading');
    setErrorMessage('');

    await waitForMapplsSdk();

    if (generation !== bootGenerationRef.current) return;

    const el = mapContainerRef.current;
    if (!el) {
      throw new Error('Map container missing');
    }

    const { width, height } = el.getBoundingClientRect();
    if (width < 8 || height < 8) {
      throw new Error('Map container is not visible');
    }

    const { lat, lng, hasCoords: hasInitialCoords } = readCenter();
    const mapCenter = hasInitialCoords ? toMapplsCenter(lat, lng) : DEFAULT_CENTER;

    const map = new window.mappls.Map(el, {
      center: mapCenter,
      zoom: hasInitialCoords ? 14 : 5,
      zoomControl: true,
      location: false,
      scrollWheel: true,
      draggable: true,
    });

    if (generation !== bootGenerationRef.current) {
      try {
        map.remove?.();
      } catch {
        /* ignore */
      }
      return;
    }

    mapInstanceRef.current = map;

    await new Promise((resolve, reject) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        if (!isMapplsMapReady(map)) return;
        settled = true;
        resolve();
      };

      if (typeof map.on === 'function') {
        map.on('load', finish);
      }

      const started = Date.now();
      const poll = () => {
        if (settled) return;
        if (isMapplsMapReady(map)) {
          finish();
          return;
        }
        if (Date.now() - started >= 12000) {
          settled = true;
          reject(new Error('Mappls map did not finish loading'));
          return;
        }
        window.requestAnimationFrame(poll);
      };
      poll();
    });

    if (generation !== bootGenerationRef.current) return;

    mapLoadedRef.current = true;
    map.resize?.();

    if (hasInitialCoords) {
      setMapplsMarker(lat, lng);
    }

    map.on?.('click', (event) => {
      const clickLat = event?.latlng?.lat ?? event?.lngLat?.lat ?? event?.lat;
      const clickLng = event?.latlng?.lng ?? event?.lngLat?.lng ?? event?.lng;
      if (!Number.isFinite(clickLat) || !Number.isFinite(clickLng)) return;
      setMapplsMarker(clickLat, clickLng);
      emitCoords(clickLat, clickLng);
    });

    setStatus('ready');
  }, [destroyMapInstance, emitCoords, readCenter, setMapplsMarker]);

  useEffect(() => {
    let cancelled = false;
    let raf = 0;

    const tryCreate = () => {
      if (cancelled || mapInstanceRef.current) return;

      const el = mapContainerRef.current;
      if (!el) {
        raf = window.requestAnimationFrame(tryCreate);
        return;
      }

      const { width, height } = el.getBoundingClientRect();
      if (width < 8 || height < 8) {
        raf = window.requestAnimationFrame(tryCreate);
        return;
      }

      createMap().catch((error) => {
        if (cancelled) return;
        destroyMapInstance();
        setStatus('error');
        setErrorMessage(error?.message || 'Mappls map failed to load');
      });
    };

    raf = window.requestAnimationFrame(tryCreate);

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(raf);
      bootGenerationRef.current += 1;
      destroyMapInstance();
    };
    // Mount once — same pattern as DayTripsPlaybackHistory map init.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status !== 'ready' || !hasCoords || !mapLoadedRef.current || !mapInstanceRef.current) {
      return;
    }

    const coordKey = `${latNum},${lngNum}`;
    if (placedCoordsRef.current === coordKey) {
      return;
    }

    mapInstanceRef.current.setCenter?.(toMapplsCenter(latNum, lngNum));
    setMapplsMarker(latNum, lngNum);
    mapInstanceRef.current.resize?.();
  }, [latitude, longitude, status, hasCoords, latNum, lngNum, setMapplsMarker]);

  const handleRetry = () => {
    bootGenerationRef.current += 1;
    destroyMapInstance();
    createMap().catch((error) => {
      destroyMapInstance();
      setStatus('error');
      setErrorMessage(error?.message || 'Mappls map failed to load');
    });
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => emitCoords(pos.coords.latitude, pos.coords.longitude),
      () => {
        /* user denied or unavailable */
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">Tap the map to set location (Mappls)</p>
        <Button type="button" variant="outline" size="sm" onClick={useMyLocation}>
          <MapPin className="mr-1.5 h-3.5 w-3.5" />
          Use my location
        </Button>
      </div>
      <div className="relative overflow-hidden rounded-xl border border-muted-foreground/20">
        {status === 'loading' && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/80">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}
        {status === 'error' && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-background/95 px-4 text-center text-sm text-muted-foreground">
            <p>Map failed to load. Enter coordinates manually or retry.</p>
            {errorMessage ? (
              <p className="text-xs text-muted-foreground/80">{errorMessage}</p>
            ) : null}
            <Button type="button" variant="outline" size="sm" onClick={handleRetry}>
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
              Retry map
            </Button>
          </div>
        )}
        <div className="h-[260px] w-full sm:h-[320px]">
          <div
            id={mapContainerId}
            ref={mapContainerRef}
            className="h-full w-full"
          />
        </div>
      </div>
    </div>
  );
}

export default function LocationMapPicker(props) {
  return (
    <MapPickerErrorBoundary
      {...props}
      fallback={(onRetry) => (
        <div className="space-y-3 rounded-xl border border-muted-foreground/20 p-4 text-sm text-muted-foreground">
          <p>Map could not be displayed. Enter latitude and longitude manually.</p>
          {onRetry ? (
            <Button type="button" variant="outline" size="sm" onClick={onRetry}>
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
              Retry map
            </Button>
          ) : null}
        </div>
      )}
    />
  );
}
